// src/components/Deposit.jsx
import React, { useEffect, useState } from "react";
import "./Deposit.css";
import { useAuth } from "../contexts/SupabaseAuthProvider";
import { useUserData } from "../contexts/UserDataProvider";

/**
 * Deposit.jsx
 * - UI left intact (as requested)
 * - Manual TX-hash flow:
 *   Step1: choose method
 *   Step2: amount
 *   Step3: show QR / address
 *   Step4: "I have sent" -> TX hash input
 *   Step5: waiting animation while submitting
 *   Step6: pending verification screen (message + Done)
 *
 * - On submit: deposit record saved with status "pending_verification"
 *   into users_data.deposit_history and users_data.transactions via
 *   updateUserDataFields() from UserDataProvider.
 */

export default function Deposit() {
  const NETWORKS = [
    { id: "binance", name: "Binance Pay", icon: "/pay/binance.png", qr: "/qr/binance-qr.png", address: "1193255434" },
    { id: "usdt_trc", name: "USDT (TRC-20)", icon: "/pay/usdt.png", qr: "/qr/usdt-trc-qr.png", address: "TJYDMdHnQVWpcLtpzxhijkbWB7KvecARMF" },
    { id: "usdt_bsc", name: "USDT (BSC-20)", icon: "/pay/usdt.png", qr: "/qr/usdt-trc-qr.png", address: "0x13a36a5f225f922f6e8b6914f800e998f18ad0b8" },
    { id: "usdt_erc", name: "USDT (ERC-20)", icon: "/pay/usdt.png", qr: "/qr/usdt-erc-qr.png", address: "0x13a36a5f225f922f6e8b6914f800e998f18ad0b8" },
    { id: "btc", name: "Bitcoin (BTC)", icon: "/pay/btc.png", qr: "/qr/btc-qr.png", address: "15oVk5gUhaANFzRWaE7eK1FEQCEej8uv3H" },
    { id: "eth", name: "Ethereum (ETH)", icon: "/pay/eth.png", qr: "/qr/eth-qr.png", address: "0x13a36a5f225f922f6e8b6914f800e998f18ad0b8" },
    { id: "ltc", name: "Litecoin (LTC)", icon: "/pay/ltc.png", qr: "/qr/ltc-qr.png", address: "LRTLqEwtVLDHJginWRBGREEaAjPofyUEZm" },
  ];

  const { user } = useAuth();
  const { userData, updateUserDataFields } = useUserData();

  const [step, setStep] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState("");
  const [timer, setTimer] = useState(24 * 60 * 60);
  const [txHash, setTxHash] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selected = NETWORKS.find((n) => n.id === selectedId) || null;

  const arr = (v) => (Array.isArray(v) ? v : []);

  const chooseNetwork = (id) => {
    setSelectedId(id);
    setAmount("");
    setTxHash("");
    setTimer(24 * 60 * 60);
    setStep(2);
  };

  const startQR = () => {
    // ensure selected and amount is valid number >= 20
    const a = Number(amount);
    if (!selected || !a || a < 20) return;
    setStep(3);
    setTimer(24 * 60 * 60);
  };

  // Save a pending deposit record to user's data
  const savePendingDeposit = async () => {
    if (!user?.id || !selected) throw new Error("Missing user or selected method");

    const amt = Math.floor(Number(amount) || 0);
    const now = Date.now();

    const item = {
      id: `DEP-${now}`,
      type: "deposit",
      amount: amt,
      network: selected.name,
      address: selected.address,
      txid: txHash?.trim() || null,
      status: "pending_verification",
      time: now,
    };

    // merge into arrays
    const deposits = arr(userData?.deposit_history);
    const txs = arr(userData?.transactions);

    // update via provider util (this should write to supabase and update UI)
    await updateUserDataFields({
      deposit_history: [item, ...deposits],
      transactions: [item, ...txs],
      updated_at: new Date().toISOString(),
    });

    return item;
  };

  // when user submits TX hash manually
  const submitTx = async () => {
    if (!txHash.trim()) return;
    if (!user?.id) {
      alert("Please login first.");
      return;
    }

    try {
      setSubmitting(true);
      setStep(5); // waiting animation

      // small UX delay
      await new Promise((res) => setTimeout(res, 600));

      // save pending deposit (writes to DB via provider)
      await savePendingDeposit();

      // show pending verification screen (message only)
      setStep(6);
    } catch (err) {
      console.error("submitTx error:", err);
      alert("Failed to submit deposit. Try again.");
      setStep(3); // go back to QR step so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    if (step === 1) return;
    if (step === 3) return setStep(2);
    if (step === 4) return setStep(3);
    setStep((s) => Math.max(1, s - 1));
  };

  useEffect(() => {
    if (step !== 3) return;
    const i = setInterval(() => {
      setTimer((t) => (t <= 1 ? 0 : t - 1));
    }, 1000);
    return () => clearInterval(i);
  }, [step]);

  const formatTime = (sec) => {
    const h = Math.floor(sec / 3600).toString().padStart(1, "0");
    const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  return (
    <div className="dep-root">
      <div className="dep-card">
        <div className="dep-header">
          {step > 1 ? (
            <button className="dep-back" onClick={goBack}>← Back</button>
          ) : (
            <div />
          )}
          <h2 className="dep-title">Deposit</h2>
          <button className="dep-reset" onClick={() => window.location.reload()}>
            Reset
          </button>
        </div>

        {/* Step 1 — Methods */}
        {step === 1 && (
          <div className="dep-methods">
            {NETWORKS.map((n) => (
              <button key={n.id} className="method-card" onClick={() => chooseNetwork(n.id)}>
                <div className="method-left"><img src={n.icon} alt="" /></div>
                <div className="method-body">
                  <div className="method-name">{n.name}</div>
                  <div className="method-sub">Fast • Secure</div>
                </div>
                <div className="method-right">›</div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2 — Amount */}
        {step === 2 && selected && (
          <div className="dep-amount">
            <div className="selected-row">
              <img src={selected.icon} alt="" />
              <div>
                <div className="sel-name">{selected.name}</div>
                <div className="sel-sub">Minimum $20</div>
              </div>
            </div>

            <label className="lbl">Amount (USD)</label>
            <input
              className="inp"
              type="number"
              min="20"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            <div className="quick-row">
              {[25, 50, 100, 200].map((v) => (
                <button key={v} className="quick" onClick={() => setAmount(String(v))}>
                  ${v}
                </button>
              ))}
            </div>

            <div className="action-row">
              <button className="btn primary" disabled={Number(amount) < 10 || !selected} onClick={startQR}>
                Generate QR
              </button>
              <button className="btn ghost" onClick={() => setSelectedId(null)}>
                Change Method
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — QR / Address */}
        {step === 3 && selected && (
          <div className="dep-qr">
            <div className="qr-wrap">
              <div className="qr-frame">
                <img src={selected.qr} alt="QR" />
              </div>
            </div>

            <div className="addr-wrap">
              <div className="send-line">
                <div className="send-label">Send exactly</div>
                <div className="send-amt">${Number(amount).toFixed(2)}</div>
              </div>

              <div className="addr-block">
                <div className="addr-title">Wallet Address</div>
                <div className="addr-text">{selected.address}</div>
              </div>

              <div className="qr-actions">
                {/* Option B: I have sent -> TX hash input */}
                <button
                  className="btn primary"
                  onClick={() => setStep(4)}
                >
                  I have sent
                </button>

                <button className="btn ghost" onClick={() => setStep(2)}>
                  Change Amount
                </button>
              </div>

              <div className="meta-row">
                <div>Expires: {formatTime(timer)}</div>
                <div>Network: {selected.name}</div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4 — TX Hash Input */}
        {step === 4 && (
          <div className="dep-amount">
            <h3 className="sel-name">Enter Transaction Hash</h3>

            <label className="lbl">TX Hash</label>
            <input
              className="inp"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              placeholder="Paste transaction hash"
            />

            <div className="action-row">
              <button
                className="btn primary"
                disabled={!txHash.trim() || submitting}
                onClick={submitTx}
              >
                {submitting ? "Submitting…" : "Submit Hash"}
              </button>
            </div>
          </div>
        )}

        {/* Step 5 — Waiting Animation */}
        {step === 5 && (
          <div className="dep-wait">
            <div className="spinner"></div>
            <p>Submitting your payment…</p>
          </div>
        )}

        {/* Step 6 — Payment Pending Verification */}
        {step === 6 && (
          <div className="dep-success">
            <div className="success-mark pending">⧗</div>
            <h3>Payment is Pending Verification</h3>
            <p>
              Your deposit will be added to your balance after successful verification.
              <br /><br />
              Typically approved within <b>15 minutes</b>.
              <br />
              If not credited, please contact support.
            </p>
            <div className="action-row">
              <button className="btn primary" onClick={() => { setStep(1); setSelectedId(null); }}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
